import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  Attendee,
  RegistrationGroup,
  Invoice,
  PaymentDetails,
} from "@/modules/eventRegistration/features/EventWaitlist/api/getEventTransactionDetails/types";
import {
  fetchEventTransactionDetailsAction,
  initiatePaymentRefundAction,
  returnEventVouchersAction,
} from "@/modules/eventRegistration/features/EventWaitlist/slices/eventTransactionsSlice";
import { VoucherRefundModalState } from "@/modules/eventRegistration/features/EventWaitlist/components/VoucherReturnModal/types";
import { RefundModalState } from "@/modules/eventRegistration/features/EventWaitlist/components/RefundModal/types";
import { UseEventTransactionsReturn } from "@/modules/eventRegistration/features/EventWaitlist/hooks/useEventTransactions/types";

interface UseEventTransactionsProps {
  eventUuid?: string;
}

export default function useEventTransactions({
  eventUuid,
}: UseEventTransactionsProps = {}): UseEventTransactionsReturn {
  const { toast } = useToast();
  const dispatch = useAppDispatch();

  // Redux state
  const {
    loadingTransactionDetails,
    transactionDetailsError,
    transactionDetails,
    loadingPaymentRefund,
    paymentRefundError,
    paymentRefundResult,
    loadingVoucherReturn,
    voucherReturnError,
    voucherReturnResult,
  } = useAppSelector((state) => state.eventTransactions);

  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  // Modal states
  const [refundModalState, setRefundModalState] =
    useState<RefundModalState | null>(null);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [isGroupRefund, setIsGroupRefund] = useState(false);
  const [refundReason, setRefundReason] = useState("");

  const [voucherRefundModalState, setVoucherRefundModalState] =
    useState<VoucherRefundModalState | null>(null);
  const [isVoucherRefundModalOpen, setIsVoucherRefundModalOpen] =
    useState(false);

  // Invoice modal states
  const [selectedInvoices, setSelectedInvoices] = useState<Invoice[]>([]);
  const [selectedPayments, setSelectedPayments] = useState<PaymentDetails[]>(
    [],
  );
  const [selectedCompanyName, setSelectedCompanyName] = useState<string>("");
  const [selectedConfirmationNumber, setSelectedConfirmationNumber] = useState<
    string | null
  >(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);

  // State for selecting invoice to refund
  const [selectedInvoiceForRefund, setSelectedInvoiceForRefund] =
    useState<Invoice | null>(null);

  // Get data from Redux store
  const registrationGroups = transactionDetails?.registrationGroups || [];
  const attendees = transactionDetails?.attendees || [];

  // Fetch transaction details on mount
  useEffect(() => {
    if (eventUuid) {
      dispatch(fetchEventTransactionDetailsAction({ uuid: eventUuid }));
    }
  }, [dispatch, eventUuid]);

  // Handle errors
  useEffect(() => {
    if (transactionDetailsError) {
      toast({
        title: "Error",
        description: transactionDetailsError,
        variant: "destructive",
      });
    }
  }, [transactionDetailsError, toast]);

  useEffect(() => {
    if (paymentRefundError) {
      toast({
        title: "Error",
        description: paymentRefundError,
        variant: "destructive",
      });
    }
  }, [paymentRefundError, toast]);

  useEffect(() => {
    if (voucherReturnError) {
      toast({
        title: "Error",
        description: voucherReturnError,
        variant: "destructive",
      });
    }
  }, [voucherReturnError, toast]);

  // Handle successful results
  useEffect(() => {
    if (paymentRefundResult) {
      toast({
        title: "Refund initiated",
        description: `Successfully initiated refund of $${paymentRefundResult.amountRequested.toFixed(2)}`,
      });
      // Refresh the transaction details
      if (eventUuid) {
        dispatch(fetchEventTransactionDetailsAction({ uuid: eventUuid }));
      }
    }
  }, [paymentRefundResult, toast, dispatch, eventUuid]);

  useEffect(() => {
    if (voucherReturnResult) {
      toast({
        title: "Vouchers returned",
        description: `Successfully returned ${voucherReturnResult.vouchersReturned} voucher(s)`,
      });
      // Refresh the transaction details
      if (eventUuid) {
        dispatch(fetchEventTransactionDetailsAction({ uuid: eventUuid }));
      }
    }
  }, [voucherReturnResult, toast, dispatch, eventUuid]);

  const toggleExpand = useCallback((groupId: string) => {
    setExpandedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId],
    );
  }, []);

  const getGroupAttendees = useCallback(
    (groupId: string): Attendee[] => {
      return attendees.filter((a) => a.registrationGroupId === groupId);
    },
    [attendees],
  );

  const getGroupDisplayName = useCallback(
    (group: RegistrationGroup): string => {
      return group.memberCompanyName || group.companyName || "Unknown Company";
    },
    [],
  );

  const handleViewInvoice = useCallback(
    (groupId: string) => {
      const group = registrationGroups.find((g) => g.id === groupId);
      if (!group) return;

      if (!group.invoices || group.invoices.length === 0) {
        toast({
          title: "No Invoice Available",
          description: "No invoices have been generated for this transaction.",
          variant: "default",
        });
        return;
      }

      setSelectedInvoices(group.invoices);
      setSelectedPayments(group.payments);
      setSelectedCompanyName(getGroupDisplayName(group));
      setSelectedConfirmationNumber(group.confirmationNumber);
      setIsInvoiceModalOpen(true);
    },
    [registrationGroups, toast, getGroupDisplayName],
  );

  const closeInvoiceModal = useCallback(() => {
    setIsInvoiceModalOpen(false);
    setSelectedInvoices([]);
    setSelectedPayments([]);
    setSelectedCompanyName("");
    setSelectedConfirmationNumber(null);
  }, []);

  const handleRefundGroup = useCallback(
    async (groupId: string) => {
      const group = registrationGroups.find((g) => g.id === groupId);
      if (!group) return;

      // Check if there are any invoices
      if (!group.invoices || group.invoices.length === 0) {
        toast({
          title: "Cannot process refund",
          description: "No invoices found for this group.",
          variant: "destructive",
        });
        return;
      }

      // Check remaining refundable amount first
      if (group.remainingRefundableAmount <= 0) {
        toast({
          title: "Cannot process refund",
          description: group.hasPendingRefunds
            ? "All funds have been refunded or are pending refund for this group."
            : "All funds have been refunded for this group.",
          variant: "destructive",
        });
        return;
      }

      // Check if there are any refundable seats
      if (group.remainingRefundableSeats <= 0) {
        toast({
          title: "Cannot process refund",
          description: group.hasPendingRefunds
            ? "All seats have been refunded or are pending refund for this group."
            : "All seats have been refunded for this group.",
          variant: "destructive",
        });
        return;
      }

      // Show warning if there are pending refunds
      if (group.hasPendingRefunds) {
        const pendingCount = group.refundHistory.filter(
          (r) => r.status === "pending" || r.status === "processing",
        ).length;
        toast({
          title: "Pending refunds detected",
          description: `There ${pendingCount === 1 ? "is" : "are"} ${pendingCount} pending refund${pendingCount === 1 ? "" : "s"} for this group. New refunds will be limited to remaining amounts.`,
          variant: "default",
        });
      }

      // If there's only one invoice, select it automatically
      if (group.invoices.length === 1) {
        setSelectedInvoiceForRefund(group.invoices[0]);
      } else {
        // TODO: Show a modal to select which invoice to refund
        // For now, we'll use the first invoice
        setSelectedInvoiceForRefund(group.invoices[0]);
        toast({
          title: "Multiple invoices found",
          description:
            "Selecting the first invoice for refund. Multiple invoice support coming soon.",
          variant: "default",
        });
      }

      const totalAmount = group.paymentAmount || 0;
      const creditCardSeats = group.paidSeatsCount || 0;
      const pricePerSeat =
        creditCardSeats > 0 ? totalAmount / creditCardSeats : 0;

      // Open the refund modal with group information including limits
      setRefundModalState({
        attendeeId: groupId,
        attendeeName: `${getGroupDisplayName(group)} (Group)`,
        amount: pricePerSeat,
        totalSeats: group.remainingRefundableSeats,
        totalAmount: group.remainingRefundableAmount,
        remainingRefundableAmount: group.remainingRefundableAmount,
        remainingRefundableSeats: group.remainingRefundableSeats,
        hasPendingRefunds: group.hasPendingRefunds,
        refundHistory: group.refundHistory,
      });

      setIsGroupRefund(true);
      setIsRefundModalOpen(true);
    },
    [registrationGroups, toast, getGroupDisplayName],
  );

  const handleRefundVouchers = useCallback(
    async (groupId: string, voucherCount: number) => {
      const group = registrationGroups.find((g) => g.id === groupId);
      if (!group) return;

      // Check if there are any vouchers to refund
      if (voucherCount <= 0) {
        toast({
          title: "Cannot process return",
          description: "No vouchers available to return",
          variant: "destructive",
        });
        return;
      }

      // Open the voucher refund modal with group information
      setVoucherRefundModalState({
        groupId: groupId,
        groupName: getGroupDisplayName(group),
        totalVouchers: voucherCount,
        vouchersToRefund: voucherCount,
        reason: "",
      });

      setIsVoucherRefundModalOpen(true);
    },
    [registrationGroups, toast, getGroupDisplayName],
  );

  const refreshData = useCallback(async () => {
    if (eventUuid) {
      dispatch(fetchEventTransactionDetailsAction({ uuid: eventUuid }));
    }
  }, [dispatch, eventUuid]);

  const closeRefundModal = useCallback(() => {
    setIsRefundModalOpen(false);
    setIsGroupRefund(false);
    setRefundModalState(null);
    setRefundReason("");
    setSelectedInvoiceForRefund(null);
  }, []);

  const closeVoucherModal = useCallback(() => {
    setIsVoucherRefundModalOpen(false);
    setVoucherRefundModalState(null);
  }, []);

  const processRefund = useCallback(
    async (
      refundType: string,
      partialAmount: number,
      notifyAttendee: boolean,
      seatsToRefund?: number,
    ) => {
      if (!refundModalState || !eventUuid || !selectedInvoiceForRefund) return;

      // TODO: Old refund modal - replaced by new TransactionsView with line item refunds
      // Commenting out to prevent compilation errors since new UI handles refunds differently
      /*
      const refundData = {
        uuid: selectedInvoiceForRefund.id,
        lineItems: [], // New format requires line items
        reason: refundReason,
        internalNotes: null,
        notifyAttendee: notifyAttendee,
      };

      dispatch(
        initiatePaymentRefundAction(refundData, () => {
          closeRefundModal();
        }),
      );
      */
      console.warn('Old refund modal - use new TransactionsView instead');
      closeRefundModal();
    },
    [
      refundModalState,
      eventUuid,
      refundReason,
      selectedInvoiceForRefund,
      dispatch,
      closeRefundModal,
    ],
  );

  const processVoucherReturn = useCallback(
    async (
      vouchersToReturn: number,
      reason: string,
      notes: string,
      notifyAdmin: boolean,
      notifyCoordinator: boolean,
    ) => {
      if (!voucherRefundModalState) return;

      const returnData = {
        uuid: voucherRefundModalState.groupId, // This is the checkout UUID
        vouchersToReturn: vouchersToReturn,
        reason: reason as
          | "attendee_cancellation"
          | "duplicate_registration"
          | "change_in_plans"
          | "administrative_adjustment"
          | "event_cancellation"
          | "other",
        notes: notes || null,
        notifyVoucherAdmin: notifyAdmin,
        notifyEventCoordinator: notifyCoordinator,
      };

      dispatch(
        returnEventVouchersAction(returnData, () => {
          closeVoucherModal();
        }),
      );
    },
    [voucherRefundModalState, dispatch, closeVoucherModal],
  );

  return {
    registrationGroups,
    attendees,
    expandedGroups,
    isLoading: loadingTransactionDetails,
    isRefreshing: loadingTransactionDetails,
    isProcessing: loadingPaymentRefund || loadingVoucherReturn,
    toggleExpand,
    handleRefundGroup,
    handleRefundVouchers,
    handleViewInvoice,
    refreshData,
    getGroupAttendees,
    getGroupDisplayName,
    // Modal states
    refundModalState,
    isRefundModalOpen,
    isGroupRefund,
    refundReason,
    voucherRefundModalState,
    isVoucherRefundModalOpen,
    // Invoice modal states
    selectedInvoices,
    selectedPayments,
    selectedCompanyName,
    selectedConfirmationNumber,
    isInvoiceModalOpen,
    // Modal handlers
    setRefundReason,
    closeRefundModal,
    closeVoucherModal,
    closeInvoiceModal,
    processRefund,
    processVoucherReturn,
  };
}
